// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Menu  (route /menu) — "Select pack size / weight" + "Choose your cut" sheet
// Opens from EVERY Add / Edit button on the menu. Two views inside one modal dialog:
//   1. PACKS — each size the catch is sold in (data/packs.ts) with its price and per-100 g rate and its own
//      ADD / − qty + control (mix sizes freely), plus a "Customized cut" row showing the chosen cut and its price.
//   2. CUTS  — "Choose your cut": every cut this catch is offered in (data/cuts.ts) with FREE / "+₹40/kg prep",
//      a "best for" chip and a description; "Apply <cut> preference" returns to the packs.
// The sheet edits a DRAFT (quantities + cut) seeded from the cart; nothing reaches the cart until "Confirm & add to
// cart". Prices are the per-kg price × the pack weight plus the cut's prep fee (per kg of the pack): no discounts,
// savings, net-weight or glaze claims are shown because none exist.
// Modal behaviour: focus moves in, Tab stays inside, Escape steps back from CUTS or closes from PACKS, backdrop / ✕
// close, focus returns to the opener, the page behind can't scroll, and it slides up (instant with reduced motion).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Check, CheckCheck, ChevronRight, Minus, Plus, Scissors, ShoppingBag, X } from "lucide-react";
import { cutOf, cutsFor, defaultCut, prepFee, prepLabel, type CutId } from "../../data/cuts";
import { PACK_SIZES, pricePer100g } from "../../data/packs";
import type { SeafoodItem } from "../../data/seafood";
import useCatchDraft, { MAX_PACKS, type Draft } from "../../hooks/useCatchDraft";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";
import { formatInr } from "../../utils/priceFormat";

interface PackSheetProps {
  /** The catch being configured; null keeps the sheet closed. */
  item: SeafoodItem | null;
  /** Packs already in the cart for this catch, by size (seeds the draft). */
  inCart: Draft;
  /** The cut already chosen for this catch in the cart ("" / undefined = none yet). */
  cutInCart?: string;
  /** Cap on packs of all sizes together (Daily Catch: the landing's packs left). */
  maxTotal?: number;
  onClose: () => void;
  /** Called with the final quantity for every size, and the chosen cut, when the customer confirms. */
  onConfirm: (quantities: Draft, cut: CutId) => void;
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function PackSheet({ item, inCart, cutInCart, maxTotal, onClose, onConfirm }: PackSheetProps) {
  const reducedMotion = usePrefersReducedMotion();
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  const [view, setView] = useState<"packs" | "cuts">("packs");
  /** Highlighted (not yet applied) option in the cuts view. */
  const [pendingCut, setPendingCut] = useState<CutId | null>(null);

  const open = item !== null;
  // The editable draft (quantities + cut) and its totals are shared with the product page.
  const { draft, setCutId, cut, alreadyInCart, unit, total, summary, empty, set, atLimit } = useCatchDraft(item, inCart, cutInCart, maxTotal);
  const cuts = item ? cutsFor(item) : [];

  // A different catch always opens on the packs view.
  useEffect(() => {
    if (item) setView("packs");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  // Escape steps back from the cuts view first; from the packs view it closes.
  const back = () => (view === "cuts" ? setView("packs") : onClose());
  const backRef = useRef(back);
  backRef.current = back;

  // Modal behaviour: remember the opener, lock page scroll, move focus in, trap Tab, handle Escape.
  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        backRef.current();
        return;
      }
      if (event.key !== "Tab" || !panel.current) return;
      const focusable = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (opener.current instanceof HTMLElement) opener.current.focus();
    };
  }, [open]);

  // Moving between views: put focus on the dialog so the change is announced and Tab starts at the top.
  useEffect(() => {
    if (open) panel.current?.focus();
  }, [view, open]);

  const confirmLabel = empty ? (alreadyInCart ? "Remove from cart" : "Choose a pack size") : alreadyInCart ? "Update cart" : "Confirm & add to cart";

  const openCuts = () => {
    setPendingCut(cut?.id ?? null);
    setView("cuts");
  };
  const pending = item && pendingCut ? cutOf(item, pendingCut) : cut;

  return (
    <AnimatePresence>
      {item && cut && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.18 }}
          onClick={onClose}
          className="fixed inset-x-0 bottom-0 top-8 z-[60] flex items-end justify-center bg-ocean-950/60 backdrop-blur-sm sm:items-center"
        >
          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pack-sheet-title"
            tabIndex={-1}
            initial={reducedMotion ? false : { y: "100%" }}
            animate={{ y: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { y: "100%" }}
            transition={{ duration: reducedMotion ? 0 : 0.26, ease: "easeOut" }}
            onClick={(event) => event.stopPropagation()}
            className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white outline-none dark:bg-ocean-900 sm:rounded-3xl"
          >
            <div aria-hidden="true" className="mx-auto mt-3 h-1.5 w-12 shrink-0 rounded-full bg-ocean-900/20 dark:bg-white/25 sm:hidden" />

            {view === "packs" ? (
              <>
                <div className="flex items-start gap-4 p-5 pb-3">
                  <img src={item.image} alt="" width={80} height={80} className="h-20 w-20 shrink-0 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <h2 id="pack-sheet-title" className="font-display text-xl font-bold leading-tight text-ocean-950 dark:text-foam-50">
                      {item.name}
                    </h2>
                    <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">
                      {item.price} {item.unit}
                    </p>
                    {item.tags.length > 0 && <p className="mt-1 text-xs font-semibold text-teal-700 dark:text-teal-300">{item.tags.join(" · ")}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-ocean-700/10 focus-visible:outline-2 focus-visible:outline-teal-600"
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                <div className="overflow-y-auto px-5 pb-4">
                  <h3 className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Select pack size / weight</h3>
                  <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">Sold by weight, priced per kg. Pick one size or mix them.</p>

                  <ul className="mt-4 flex flex-col gap-3">
                    {PACK_SIZES.map((pack) => {
                      const qty = draft[pack.id];
                      const selected = qty > 0;
                      const fee = prepFee(cut, pack);
                      return (
                        <li
                          key={pack.id}
                          role="group"
                          aria-label={`${pack.label} pack`}
                          className={`flex items-center justify-between gap-3 rounded-2xl p-4 transition-colors ${
                            selected ? "bg-white shadow-md ring-2 ring-ocean-800 dark:bg-ocean-800" : "bg-ocean-700/5 dark:bg-white/10"
                          }`}
                        >
                          <div>
                            <p className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{pack.label}</p>
                            <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">
                              <span className="font-bold text-ocean-950 dark:text-foam-50">{formatInr(unit(pack))}</span> · {formatInr(pricePer100g(item))}/100 g
                            </p>
                            {fee > 0 && <p className="text-xs text-ocean-800/75 dark:text-foam-100/70">includes {formatInr(fee)} cut prep</p>}
                          </div>
                          {selected ? (
                            <div className="inline-flex items-center rounded-full bg-ocean-700/10 dark:bg-white/10">
                              <button
                                type="button"
                                onClick={() => set(pack.id, qty - 1)}
                                aria-label={`Decrease ${pack.label} packs`}
                                className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-ocean-700/10 focus-visible:outline-2 focus-visible:outline-teal-600"
                              >
                                <Minus className="h-4 w-4" aria-hidden="true" />
                              </button>
                              <span aria-live="polite" className="min-w-8 text-center text-base font-bold tabular-nums">
                                {qty}
                                <span className="sr-only"> of the {pack.label} pack</span>
                              </span>
                              <button
                                type="button"
                                onClick={() => set(pack.id, qty + 1)}
                                disabled={qty >= MAX_PACKS || atLimit}
                                aria-label={`Increase ${pack.label} packs`}
                                className="flex h-11 w-11 items-center justify-center rounded-full bg-ocean-800 text-white hover:bg-ocean-700 disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-teal-600"
                              >
                                <Plus className="h-4 w-4" aria-hidden="true" />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => set(pack.id, 1)}
                              disabled={atLimit}
                              aria-label={`Add a ${pack.label} pack`}
                              className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-ocean-700/10 px-5 text-sm font-bold text-ocean-900 hover:bg-ocean-700/15 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-white/15 dark:text-foam-50"
                            >
                              <Plus className="h-4 w-4" aria-hidden="true" />
                              ADD
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  <button
                    type="button"
                    onClick={openCuts}
                    aria-label={`Cut: ${cut.name}, ${prepLabel(cut)}. Select cut`}
                    className="mt-4 flex w-full items-center gap-3 rounded-2xl bg-ocean-700/5 p-4 text-left hover:bg-ocean-700/10 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-white/10"
                  >
                    <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
                      <Scissors className="h-5 w-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-bold text-ocean-950 dark:text-foam-50">Customized cut</span>
                      <span className="block truncate text-sm text-ocean-800/85 dark:text-foam-100/80">
                        {cut.name} · {prepLabel(cut)}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-0.5 text-sm font-bold text-ocean-800 dark:text-foam-100">
                      {cuts.length > 1 ? "Select cut" : "Cut"}
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                  </button>
                </div>

                <div className="border-t border-ocean-900/10 bg-white p-5 dark:border-white/10 dark:bg-ocean-900">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-widest text-ocean-800/70 dark:text-foam-100/70">Item total</p>
                      <p className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50" aria-live="polite">{formatInr(total)}</p>
                    </div>
                    <p className="pb-1 text-right text-sm text-ocean-800/85 dark:text-foam-100/80">{empty ? "Nothing selected" : `for ${summary}`}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onConfirm(draft, cut.id)}
                    disabled={empty && !alreadyInCart}
                    className="mt-4 flex min-h-14 w-full items-center justify-between gap-3 rounded-full bg-ocean-800 px-6 text-base font-bold text-white shadow-md hover:bg-ocean-700 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                  >
                    <span className="flex items-center gap-2">
                      <ShoppingBag className="h-5 w-5" aria-hidden="true" />
                      {confirmLabel}
                    </span>
                    {!empty && <span>{formatInr(total)}</span>}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 p-5 pb-3">
                  <button
                    type="button"
                    onClick={() => setView("packs")}
                    aria-label="Back to pack sizes"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-ocean-700/10 focus-visible:outline-2 focus-visible:outline-teal-600"
                  >
                    <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <h2 id="pack-sheet-title" className="font-display text-xl font-bold leading-tight text-ocean-950 dark:text-foam-50">
                      Choose your cut
                    </h2>
                    <p className="truncate text-sm text-ocean-800/85 dark:text-foam-100/80">{item.name}</p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-ocean-700/10 focus-visible:outline-2 focus-visible:outline-teal-600"
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                <div className="overflow-y-auto px-5 pb-4">
                  <ul role="radiogroup" aria-label="Cut" className="flex flex-col gap-3">
                    {cuts.map((option) => {
                      const selected = option.id === pending?.id;
                      return (
                        <li key={option.id}>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => setPendingCut(option.id)}
                            className={`flex w-full items-start gap-3 rounded-2xl p-4 text-left transition-colors focus-visible:outline-2 focus-visible:outline-teal-600 ${
                              selected ? "bg-white shadow-md ring-2 ring-ocean-800 dark:bg-ocean-800" : "bg-ocean-700/5 hover:bg-ocean-700/10 dark:bg-white/10"
                            }`}
                          >
                            <span
                              aria-hidden="true"
                              className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${selected ? "bg-ocean-800 text-white" : "bg-ocean-700/15"}`}
                            >
                              {selected && <Check className="h-4 w-4" />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-start justify-between gap-3">
                                <span className="font-display text-base font-bold leading-snug text-ocean-950 dark:text-foam-50">{option.name}</span>
                                <span className={`shrink-0 text-sm font-bold ${option.prepFeePerKg > 0 ? "text-ocean-800 dark:text-foam-100" : "text-teal-700 dark:text-teal-300"}`}>
                                  {prepLabel(option)}
                                </span>
                              </span>
                              {option.tag && (
                                <span className="mt-1.5 inline-block rounded-full bg-ocean-700/10 px-2.5 py-0.5 text-xs font-bold text-ocean-800 dark:bg-white/15 dark:text-foam-100">
                                  {option.tag}
                                </span>
                              )}
                              <span className="mt-1.5 block text-sm text-ocean-800/85 dark:text-foam-100/80">{option.description}</span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="border-t border-ocean-900/10 bg-white p-5 dark:border-white/10 dark:bg-ocean-900">
                  <button
                    type="button"
                    onClick={() => {
                      if (pending) setCutId(pending.id);
                      setView("packs");
                    }}
                    className="flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-ocean-800 px-6 text-base font-bold text-white shadow-md hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                  >
                    <CheckCheck className="h-5 w-5" aria-hidden="true" />
                    Apply {(pending ?? defaultCut(item)).name} preference
                  </button>
                  <p className="mt-3 text-center text-xs text-ocean-800/80 dark:text-foam-100/75">
                    Your cut is saved with your order line so our team can prepare it that way.
                  </p>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
